import {
  Assessment,
  Description,
  Euro,
  ExpandMore,
  Gavel,
  History,
  Map as MapIcon,
  People,
  Terrain,
  Warning,
} from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  RadioGroup,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { Close } from '@mui/icons-material';
import dayjs from 'dayjs';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { OpenStreetMapProvider } from 'leaflet-geosearch';
import { NumericFormat } from 'react-number-format';
import { ILegalTransactionRelationshipRealEstate } from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/ILegalTransactionRelationshipRealEstate';
import {
  IRealEstatePurchaseContract,
  initRealEstatePurchaseContract,
} from '../../Interfaces/ILegalTransactionRelationship/ILegalTransactionRelationshipRealEstate/IRealEstatePurchaseContract';
import { ILegalTransactionFullObject } from '../../Interfaces/ILegalTransaction';
import { ILegalPhaseFullObject } from '../../Interfaces/ILegalPhase';
import { ILegalRequirementState } from '../../Interfaces/ILegalRequirementState';
import { RealEstateInventoryOptions } from '../PurchaseContract/RealEstate/InventoryOption/RealEstateInventoryOptions';
import { RealEstatePropertyTypes } from '../PurchaseContract/RealEstate/RealEstatePropertyTypes';
import { validateArea } from '../PurchaseContract/utilities';
import { QueryLandRegisterDialog } from '../LandRegisterQuery/QueryLandRegisterDialog';
import { EigentuemerRechercheDialog } from '../LandRegisterQuery/EigentuemerRechercheDialog';
import { LandRegisterMainQueryDocument } from '../LandRegisterQuery/LandRegisterMainQueryDocument';
import { RealEstateOwners } from './RealEstateOwners';
import { RealEstateMap } from './RealEstateMap';
import { usePurchaseContractMeta } from '../PurchaseContract/hooks/usePurchaseContractMeta';
import {
  useAdministrativeUnits,
  useCourtAdministrativeUnitAssignments,
  useCourts,
} from '../../Aemter/hooks';
import {
  IBlattRow,
  IGrundbuchblattFullObject,
  useBlattForRealEstate,
  useGrundbuchblattFullObject,
  useSaveGrundbuchblattFullObject,
} from '../../hooks/reactQuerys/GrundbuchblattQueries';
import { BlattRowsEditor } from './BlattRowsEditor';
import {
  useGetRealEstateLandRegisterEntryQueryByRealEstate,
  useRelatedRealEstates,
} from '../../hooks/reactQuerys/RealEstateQueries';
import { RealEstateBorisLookup } from './RealEstateBorisLookup';
import { customFetcher } from '../../hooks/useFetch';
import { ActivityLog } from '../../generic/ActivityLog';

/** Bereiche des Grundbuchblatt-Protokolls. */
const LAND_REGISTER_LOG_AREAS = [
  { label: 'Grundbuchblatt', entityTypes: ['Grundbuchblatt'] },
  {
    label: 'Grundstücke',
    entityTypes: ['Grundstueck', 'RealEstate', 'RealEstateLandParcel'],
  },
  {
    label: 'Eigentümer',
    entityTypes: ['GrundbuchblattOwner', 'LegalClientGrundbuchblatt'],
  },
  { label: 'Vorgänge', entityTypes: ['LegalTransaction'] },
];

interface IProps {
  realEstate: ILegalTransactionRelationshipRealEstate;
  onSave: (updated: ILegalTransactionRelationshipRealEstate) => void;
  /** #1174: Callback nach dem Speichern des Grundbuchblatts (auch Entwurf ohne
   *  Blattnummer) — zum blatt-zentrischen Verknüpfen mit dem Vorgang. */
  onBlattSaved?: (idGrundbuchblatt: number, blattnummer: string) => void;
  onClose: () => void;
  legalTransaction?: ILegalTransactionFullObject;
  legalPhaseFullObjectArray?: ILegalPhaseFullObject[];
  setLegalPhaseFullObjectArray?: Function;
  legalReqStates?: ILegalRequirementState[];
}

interface ISectionProps {
  title: string;
  description?: string;
  defaultExpanded?: boolean;
  children: React.ReactNode;
}

const Section: React.FC<ISectionProps> = ({
  title,
  description,
  defaultExpanded,
  children,
}) => (
  <Accordion
    defaultExpanded={defaultExpanded}
    disableGutters
    elevation={0}
    sx={{
      mb: 1,
      border: 1,
      borderColor: 'divider',
      borderRadius: 1,
      '&:before': { display: 'none' },
    }}
  >
    <AccordionSummary expandIcon={<ExpandMore />}>
      <Typography variant='subtitle1' fontWeight={600}>
        {title}
      </Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ pt: 0 }}>
      {description && (
        <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
          {description}
        </Typography>
      )}
      {children}
    </AccordionDetails>
  </Accordion>
);

const MENU = [
  { key: 'parcels', label: 'Flurstück', icon: <Terrain fontSize='small' /> },
  { key: 'map', label: 'Karte', icon: <MapIcon fontSize='small' /> },
  { key: 'landregister', label: 'Grundbuch', icon: <Gavel fontSize='small' /> },
  { key: 'queries', label: 'Abrufe', icon: <Description fontSize='small' /> },
  { key: 'owners', label: 'Eigentümer', icon: <People fontSize='small' /> },
  {
    key: 'contract',
    label: 'Kaufvertrag',
    icon: <Euro fontSize='small' />,
  },
  {
    key: 'valuation',
    label: 'Bewertung',
    icon: <Assessment fontSize='small' />,
  },
  {
    key: 'object',
    label: 'Objektdaten',
    icon: <Description fontSize='small' />,
  },
  { key: 'log', label: 'Log', icon: <History fontSize='small' /> },
] as const;

export const RealEstateEditDialog: React.FC<IProps> = ({
  realEstate,
  onSave,
  onBlattSaved,
  onClose,
  legalTransaction,
  legalPhaseFullObjectArray,
  setLegalPhaseFullObjectArray,
  legalReqStates,
}) => {
  const [formValues, setFormValues] = useState(realEstate);
  const [tab, setTab] = useState(0);
  const [isWarningPrice, setIsWarningPrice] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const [purchaseContract, setPurchaseContract] =
    useState<IRealEstatePurchaseContract | null>(() => {
      if (!legalTransaction) return null;
      return (
        realEstate.PurchaseContractArray?.find(
          p => p.idLegalTransaction === legalTransaction.idLegalTransaction
        ) ??
        initRealEstatePurchaseContract(
          realEstate.idRealEstate,
          legalTransaction.idLegalTransaction
        )
      );
    });

  const {
    propertyTypes,
    inventoryOptions,
    realEstateUseTypes,
    purchaseContractRelationTypes,
  } = usePurchaseContractMeta();
  const { administrativeUnits } = useAdministrativeUnits();
  const { courtAdministrativeUnitAssignments } =
    useCourtAdministrativeUnitAssignments();
  const {
    realEstateLandRegisterEntryQueries,
    refetchRealEstateLandRegisterEntryQueries,
  } = useGetRealEstateLandRegisterEntryQueryByRealEstate(
    realEstate.idRealEstate
  );
  const { relatedRealEstates } = useRelatedRealEstates(realEstate.idRealEstate);
  const adminUnit = administrativeUnits ?? [];
  const metaReady = !!(
    propertyTypes &&
    inventoryOptions &&
    realEstateUseTypes &&
    purchaseContractRelationTypes
  );
  const queries = realEstateLandRegisterEntryQueries ?? [];
  const hasVorgangContext = !!legalTransaction;

  // --- Blatt-zentrisch (#1108): Grundbuchblatt zum Grundstück laden/bearbeiten ---
  const { courts } = useCourts();
  const { resolvedIdGrundbuchblatt, blattResolveSettled } =
    useBlattForRealEstate(
      realEstate.idRealEstate > 0 ? realEstate.idRealEstate : undefined
    );
  const { blattFullObject } = useGrundbuchblattFullObject(
    resolvedIdGrundbuchblatt ?? undefined
  );
  const { saveBlatt } = useSaveGrundbuchblattFullObject();

  const [blattId, setBlattId] = useState<number | null>(null);
  const [blattnummer, setBlattnummer] = useState('');
  const [blattCourt, setBlattCourt] = useState<number | ''>('');
  const [blattAdminUnit, setBlattAdminUnit] = useState<number | ''>('');
  const [blattRows, setBlattRows] = useState<IBlattRow[]>([]);
  const blattInitDone = useRef(false);

  useEffect(() => {
    if (blattInitDone.current) return;
    if (!blattResolveSettled) return; // auf Resolve warten
    if (resolvedIdGrundbuchblatt) {
      if (!blattFullObject) return; // auf fullObject warten
      blattInitDone.current = true;
      setBlattId(blattFullObject.idGrundbuchblatt);
      setBlattnummer(blattFullObject.Blattnummer ?? '');
      setBlattCourt(blattFullObject.idCourt ?? '');
      setBlattAdminUnit(blattFullObject.idAdministrativeUnit ?? '');
      setBlattRows(blattFullObject.RowArray ?? []);
      return;
    }
    // Kein Blatt vorhanden → aus dem Grundstück ableiten (Neuanlage/Erstkontakt)
    blattInitDone.current = true;
    const fe = (realEstate.LandParcelEntryArray ?? [])[0];
    setBlattCourt(fe?.idCourt ?? '');
    setBlattAdminUnit(fe?.idAdministrativeUnit ?? '');
    setBlattnummer(fe?.SheetLandRegister ?? '');
    const reAddr =
      realEstate.Street || realEstate.StreetNr || realEstate.idPostcode;
    const rows: IBlattRow[] = (realEstate.LandParcelEntryArray ?? []).map(
      e => ({
        idRealEstateLandRegisterEntry:
          e.idRealEstateLandRegisterEntry > 0
            ? e.idRealEstateLandRegisterEntry
            : null,
        Parcel: e.Parcel,
        LandParcel: e.LandParcel,
        idRealEstate:
          realEstate.idRealEstate > 0 ? realEstate.idRealEstate : null,
        Street: realEstate.Street,
        StreetNr: realEstate.StreetNr,
        idPostcode: realEstate.idPostcode,
        ZipCode: realEstate.Postcode,
        Place: realEstate.City,
        Latitude: realEstate.Latitude,
        Longitude: realEstate.Longitude,
      })
    );
    if (rows.length === 0 && reAddr) {
      rows.push({
        idRealEstateLandRegisterEntry: null,
        Parcel: '',
        LandParcel: '',
        idRealEstate:
          realEstate.idRealEstate > 0 ? realEstate.idRealEstate : null,
        Street: realEstate.Street,
        StreetNr: realEstate.StreetNr,
        idPostcode: realEstate.idPostcode,
        ZipCode: realEstate.Postcode,
        Place: realEstate.City,
        Latitude: realEstate.Latitude,
        Longitude: realEstate.Longitude,
      });
    }
    setBlattRows(rows);
  }, [
    blattResolveSettled,
    resolvedIdGrundbuchblatt,
    blattFullObject,
    realEstate,
  ]);

  // Gemarkungen zum gewählten Amtsgericht (Rolle 3)
  const blattGemarkungOptions = useMemo(() => {
    const all = administrativeUnits ?? [];
    if (!blattCourt) return all;
    const assigned = new Set(
      (courtAdministrativeUnitAssignments ?? [])
        .filter(a => a.idCourt === blattCourt && a.idCourtRole === 3)
        .map(a => a.idAdministrativeUnit)
    );
    const filtered = all.filter(u => assigned.has(u.idAdministrativeUnit));
    return filtered.length > 0 ? filtered : all;
  }, [administrativeUnits, courtAdministrativeUnitAssignments, blattCourt]);

  // Karte: ohne gespeicherte Koordinaten die Adresse via OSM (Nominatim,
  // leaflet-geosearch) geocodieren, damit die Karte die Lage trotzdem zeigt.
  const [geocoded, setGeocoded] = useState<{ lat: number; lng: number } | null>(
    null
  );
  const [geocoding, setGeocoding] = useState(false);
  const hasStoredCoords =
    formValues.Latitude != null && formValues.Longitude != null;
  const geoQuery = [
    [formValues.Street, formValues.StreetNr].filter(Boolean).join(' ').trim(),
    [formValues.Postcode, formValues.City].filter(Boolean).join(' ').trim(),
  ]
    .filter(Boolean)
    .join(', ')
    .trim();
  // Ausgangs-Adresse merken, um spätere Änderungen (z. B. PLZ) zu erkennen.
  const baselineGeoQuery = useRef<string | null>(null);
  useEffect(() => {
    if (baselineGeoQuery.current === null && geoQuery.length >= 4)
      baselineGeoQuery.current = geoQuery;
  }, [geoQuery]);
  const addressChanged =
    baselineGeoQuery.current !== null && baselineGeoQuery.current !== geoQuery;

  useEffect(() => {
    // Geocodieren, wenn keine gespeicherten Koordinaten vorliegen ODER die
    // Adresse (z. B. PLZ) geändert wurde — dann folgt die Karte der neuen Lage.
    if (geoQuery.length < 4 || (hasStoredCoords && !addressChanged)) {
      setGeocoded(null);
      return;
    }
    let cancelled = false;
    setGeocoding(true);
    new OpenStreetMapProvider()
      .search({ query: geoQuery })
      .then(res => {
        if (!cancelled) {
          setGeocoded(
            res[0] ? { lat: Number(res[0].y), lng: Number(res[0].x) } : null
          );
        }
      })
      .catch(() => {
        if (!cancelled) setGeocoded(null);
      })
      .finally(() => {
        if (!cancelled) setGeocoding(false);
      });
    return () => {
      cancelled = true;
    };
  }, [geoQuery, hasStoredCoords, addressChanged]);

  const isAreaValid = useMemo(
    () => validateArea(formValues, purchaseContract ?? undefined),
    [
      formValues.hasPartialArea,
      formValues.isMeasured,
      formValues.Area,
      purchaseContract,
    ]
  );

  const hasBuildPropertyType = useMemo(
    () => formValues.PropertyTypeArray.some(x => x.idPropertyType === 1),
    [formValues.PropertyTypeArray]
  );
  const isOnlyUndevelopedProperty = useMemo(
    () =>
      formValues.PropertyTypeArray.length === 1 &&
      formValues.PropertyTypeArray.some(x => x.idPropertyType === 2),
    [formValues.PropertyTypeArray]
  );

  const entries = formValues.LandParcelEntryArray ?? [];
  const firstEntry = entries[0];
  const gemarkungLabel = (e?: typeof firstEntry) =>
    !e
      ? null
      : e.idAdministrativeUnit === null
        ? e.LandRegister || null
        : (adminUnit.find(
            a => a.idAdministrativeUnit === e.idAdministrativeUnit
          )?.AdministrativeUnit ?? null);
  const hasBlatt = entries.some(e => (e.SheetLandRegister ?? '').trim() !== '');
  const addressTitle =
    `${formValues.Street ?? ''} ${formValues.StreetNr ?? ''}`.trim() +
    (formValues.City
      ? `, ${formValues.Postcode ?? ''} ${formValues.City}`
      : '');

  const headChips = [
    gemarkungLabel(firstEntry)
      ? `Gemarkung ${gemarkungLabel(firstEntry)}`
      : null,
    firstEntry?.Parcel ? `Flur ${firstEntry.Parcel}` : null,
    firstEntry?.LandParcel ? `Flurstück ${firstEntry.LandParcel}` : null,
    hasBlatt ? `Blatt ${firstEntry?.SheetLandRegister}` : null,
  ].filter(Boolean) as string[];

  const setOwners = (
    owners: ILegalTransactionRelationshipRealEstate['OwnerArray']
  ) => setFormValues(x => ({ ...x, OwnerArray: owners }));

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Aktuellen Kaufvertrag in PurchaseContractArray einbetten, damit er auch
      // bei der Neuanlage (noch keine idRealEstate) über den Vorgangs-Save
      // persistiert wird.
      let updated = formValues;
      if (purchaseContract) {
        const others = (formValues.PurchaseContractArray ?? []).filter(
          p => p.idLegalTransaction !== purchaseContract.idLegalTransaction
        );
        updated = {
          ...formValues,
          PurchaseContractArray: [...others, purchaseContract],
        };
      }
      // Bestehendes Grundstück (positive id): Brücke sofort speichern.
      // Neue Grundstücke haben noch keine id → kein PUT (würde 500 werfen),
      // die Persistierung übernimmt der Vorgangs-Save (relationship.py).
      if (legalTransaction && purchaseContract && formValues.idRealEstate > 0) {
        await customFetcher({
          endpoint: `/realestate/${formValues.idRealEstate}/purchasecontracts`,
          method: 'PUT',
          body: purchaseContract,
        });
      }
      // Blatt-zentrisch: Grundbuchblatt + gekoppelte Zeilen direkt persistieren.
      // #1174: Blatt auch OHNE Blattnummer speichern (Entwurf), sobald
      // überhaupt Blatt-Daten vorliegen (Gericht/Gemarkung/Flurstücke) — sonst
      // gingen die Eingaben verloren und tauchten in der Übersicht nicht auf.
      const hasBlattData =
        blattnummer.trim() !== '' ||
        blattCourt !== '' ||
        blattAdminUnit !== '' ||
        (blattRows?.length ?? 0) > 0;
      let savedBlatt: IGrundbuchblattFullObject | undefined;
      if (hasBlattData) {
        try {
          savedBlatt = await saveBlatt({
            idGrundbuchblatt: blattId ?? -1,
            Blattnummer: blattnummer.trim(),
            idCourt: blattCourt === '' ? null : blattCourt,
            idAdministrativeUnit: blattAdminUnit === '' ? null : blattAdminUnit,
            RowArray: blattRows,
          });
          // Persistierte IDs zurückspielen, damit ein erneutes Speichern die
          // Zeilen aktualisiert statt neue Flurstück-Einträge anzulegen
          // (sonst wuchs die Liste bei jedem Speichern).
          if (savedBlatt?.idGrundbuchblatt) {
            setBlattId(savedBlatt.idGrundbuchblatt);
            if (Array.isArray(savedBlatt.RowArray))
              setBlattRows(savedBlatt.RowArray);
            // Blatt-zentrisch an den Vorgang verknüpfen (auch Entwurf), damit es
            // in der Gegenstandsübersicht (#1166) erscheint.
            onBlattSaved?.(
              savedBlatt.idGrundbuchblatt,
              savedBlatt.Blattnummer ?? ''
            );
          }
        } catch {
          /* Blatt-Persistierung darf den Grundstuecks-Save nicht blockieren */
        }
      }
      // Flur/Flurstück sind am Blatt autoritativ. Der nachgelagerte Vorgangs-Save
      // (Asset → RealEstate.LandParcelEntryArray) würde sonst die soeben am Blatt
      // gespeicherten Werte mit dem (veralteten) Stand aus formValues
      // überschreiben. Daher die übergebenen Flurstück-Werte mit dem gespeicherten
      // Blatt-Stand abgleichen (Match über idRealEstateLandRegisterEntry).
      if (savedBlatt && Array.isArray(savedBlatt.RowArray)) {
        const blattById = new Map(
          savedBlatt.RowArray.filter(r => r.idRealEstateLandRegisterEntry).map(
            r => [r.idRealEstateLandRegisterEntry, r] as const
          )
        );
        updated = {
          ...updated,
          LandParcelEntryArray: (updated.LandParcelEntryArray ?? []).map(e => {
            const r = blattById.get(e.idRealEstateLandRegisterEntry);
            return r
              ? {
                  ...e,
                  Parcel: r.Parcel ?? e.Parcel,
                  LandParcel: r.LandParcel ?? e.LandParcel,
                }
              : e;
          }),
        };
      }
      onSave(updated);
    } finally {
      setIsSaving(false);
    }
  };

  const renderContent = () => {
    switch (MENU[tab].key) {
      case 'parcels':
        return <BlattRowsEditor rows={blattRows} setRows={setBlattRows} />;
      case 'map': {
        const relatedMarkers = relatedRealEstates
          .filter(r => r.Latitude != null && r.Longitude != null)
          .map(r => ({
            lat: r.Latitude as number,
            lng: r.Longitude as number,
            label:
              [r.Street, r.StreetNr].filter(Boolean).join(' ').trim() ||
              (r.LandRegister
                ? `Gemarkung ${r.LandRegister}${
                    r.SheetLandRegister ? `, Blatt ${r.SheetLandRegister}` : ''
                  }`
                : `Grundstück #${r.idRealEstate}`),
          }));
        // Bei geänderter Adresse (z. B. PLZ) der frisch geocodierten Lage folgen,
        // sonst die gespeicherten Koordinaten bevorzugen.
        const mapLat =
          (addressChanged
            ? geocoded?.lat
            : (formValues.Latitude ?? geocoded?.lat)) ?? null;
        const mapLng =
          (addressChanged
            ? geocoded?.lng
            : (formValues.Longitude ?? geocoded?.lng)) ?? null;
        if (mapLat != null && mapLng != null) {
          return (
            <Stack spacing={1}>
              <RealEstateMap
                key={`map-${mapLat}-${mapLng}`}
                lat={mapLat}
                lng={mapLng}
                label={addressTitle || `Grundstück #${formValues.idRealEstate}`}
                related={relatedMarkers}
              />
              {(addressChanged || !hasStoredCoords) && geocoded && (
                <Typography variant='caption' color='text.secondary'>
                  Lage anhand der Adresse ermittelt (ungefähr). Exakte
                  Koordinaten via BORIS im Tab „Grundbuch".
                </Typography>
              )}
              {relatedMarkers.length > 0 && (
                <Typography variant='caption' color='text.secondary'>
                  Rot markiert: {relatedMarkers.length} verwandte(s)
                  Grundstück(e) (gleicher Eigentümer oder gleiches Blatt).
                </Typography>
              )}
            </Stack>
          );
        }
        if (geocoding) {
          return (
            <Stack alignItems='center' spacing={1} sx={{ py: 6 }}>
              <CircularProgress />
              <Typography variant='body2' color='text.secondary'>
                Adresse wird auf der Karte gesucht …
              </Typography>
            </Stack>
          );
        }
        return (
          <Alert severity='info' variant='outlined'>
            Für dieses Grundstück ist noch keine Adresse/Lage hinterlegt, daher
            kann die Karte nichts anzeigen. Die Adresse wird bei den Stammdaten
            des Grundstücks erfasst; exakte Koordinaten lassen sich über BORIS
            im Tab „Grundbuch" ermitteln.
          </Alert>
        );
      }

      case 'landregister':
        return (
          <Stack spacing={2}>
            {!hasBlatt && (
              <Alert severity='warning' icon={<Warning />}>
                <AlertTitle>Keine Blattnummer hinterlegt</AlertTitle>
                Für dieses Grundstück ist noch keine Blattnummer hinterlegt.
                Führen Sie eine <strong>Eigentümerrecherche</strong> durch, um
                die Grundbuchinformationen zu ermitteln.
              </Alert>
            )}

            <Box
              sx={theme => ({
                p: 1.5,
                borderRadius: 1,
                border: `1px solid ${theme.palette.divider}`,
              })}
            >
              <Stack
                direction='row'
                alignItems='center'
                spacing={1}
                flexWrap='wrap'
                useFlexGap
              >
                <Chip size='small' color='primary' label='Grundbuch' />
                <Typography variant='subtitle2'>Eigentümerrecherche</Typography>
                <Typography
                  variant='caption'
                  color='text.secondary'
                  sx={{ flexGrow: 1 }}
                >
                  Eigentümer über das Grundbuchportal ermitteln (Schritt 1 — vor
                  den Abrufen)
                </Typography>
                {hasVorgangContext ? (
                  <EigentuemerRechercheDialog
                    realEstateObject={formValues}
                    setRealEstateObject={(
                      updated: ILegalTransactionRelationshipRealEstate
                    ) => setFormValues(updated)}
                    legalTransaction={legalTransaction!}
                  />
                ) : (
                  <Typography variant='caption' color='text.secondary'>
                    nur im Vorgang verfügbar
                  </Typography>
                )}
              </Stack>
            </Box>

            <Section title='Bodenrichtwert & Verortung (BORIS)' defaultExpanded>
              <Typography variant='body2' color='text.secondary' sx={{ mb: 2 }}>
                Flur und Flurstück werden ausschließlich im Tab „Flurstück"
                erfasst. Hier lassen sich Bodenrichtwert und Koordinaten über
                BORIS ermitteln.
              </Typography>
              <RealEstateBorisLookup
                realEstate={formValues}
                setRealEstate={setFormValues}
                courtAdminAssignments={courtAdministrativeUnitAssignments ?? []}
              />
            </Section>
          </Stack>
        );

      case 'queries':
        if (!hasVorgangContext) {
          return (
            <Alert severity='info' variant='outlined'>
              Abrufe sind nur im Vorgangskontext verfügbar.
            </Alert>
          );
        }
        return (
          <Stack spacing={2}>
            <Typography variant='body2' color='text.secondary'>
              Abrufe beim Grundbuchportal — Markentabelle, Aktualitätsnachweis
              und Grundbuchauszug (Schritt 2 — nach der Eigentümerrecherche).
            </Typography>
            {!hasBlatt ? (
              <Alert severity='warning' icon={<Warning />}>
                <AlertTitle>Zuerst Eigentümerrecherche durchführen</AlertTitle>
                Für Abrufe wird eine Blattnummer benötigt. Führen Sie zuerst im
                Tab „Grundbuch" die Eigentümerrecherche durch — danach sind die
                Abrufe hier möglich.
              </Alert>
            ) : (
              <Box>
                <QueryLandRegisterDialog
                  realEstateObject={formValues}
                  setRealEstateObject={(
                    updated: ILegalTransactionRelationshipRealEstate
                  ) => setFormValues(updated)}
                  realEstateLandRegisterEntryQueries={queries}
                  refetchRealEstateLandRegisterEntryQueries={
                    refetchRealEstateLandRegisterEntryQueries
                  }
                  legalTransaction={legalTransaction!}
                  mode='abruf'
                />
              </Box>
            )}
            {legalPhaseFullObjectArray &&
              legalReqStates &&
              setLegalPhaseFullObjectArray && (
                <>
                  <Divider />
                  <LandRegisterMainQueryDocument
                    realEstateLandRegisterEntryQueries={queries}
                    legalPhaseFullObjectArray={legalPhaseFullObjectArray}
                    legalReqStates={legalReqStates}
                    setLegalPhaseFullObjectArray={setLegalPhaseFullObjectArray}
                  />
                </>
              )}
          </Stack>
        );

      case 'owners':
        return (
          <RealEstateOwners
            owners={formValues.OwnerArray ?? []}
            setOwners={setOwners}
          />
        );

      case 'contract':
        return (
          <Stack spacing={0}>
            {!hasVorgangContext || !purchaseContract ? (
              <Alert severity='info' variant='outlined'>
                Kaufvertragsdaten (Preis, Vermessung, Inventar, Räumung,
                Vermietung) sind nur im Vorgangskontext verfügbar.
              </Alert>
            ) : (
              <>
                {/* Preis & Konditionen */}
                <Section title='Preis & Konditionen' defaultExpanded>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <NumericFormat
                        value={purchaseContract.Price}
                        suffix=' €'
                        fixedDecimalScale
                        thousandSeparator='.'
                        decimalSeparator=','
                        fullWidth
                        customInput={TextField}
                        size='small'
                        label='Kaufpreis'
                        allowNegative={false}
                        error={isWarningPrice}
                        onValueChange={values => {
                          if (values.floatValue !== undefined) {
                            setIsWarningPrice(false);
                            setPurchaseContract(x =>
                              x ? { ...x, Price: values.floatValue ?? 0 } : x
                            );
                          } else {
                            setIsWarningPrice(true);
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <NumericFormat
                        value={purchaseContract.PriceInventoryOption ?? ''}
                        suffix=' €'
                        thousandSeparator='.'
                        decimalSeparator=','
                        fullWidth
                        customInput={TextField}
                        size='small'
                        label='Anteil Anlagen vom Kaufpreis'
                        allowNegative={false}
                        disabled={
                          purchaseContract.InventoryOptionArray.length === 0
                        }
                        onValueChange={values =>
                          setPurchaseContract(x =>
                            x
                              ? {
                                  ...x,
                                  PriceInventoryOption:
                                    values.floatValue ?? null,
                                }
                              : x
                          )
                        }
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl fullWidth size='small'>
                        <InputLabel>Nutzungsüberlassung</InputLabel>
                        <Select
                          label='Nutzungsüberlassung'
                          value={formValues.idRealEstateUseType}
                          onChange={({ target: { value } }) =>
                            setFormValues(x => ({
                              ...x,
                              idRealEstateUseType: Number(value),
                            }))
                          }
                        >
                          {(realEstateUseTypes ?? []).map(t => (
                            <MenuItem
                              key={t.idRealEstateUseType}
                              value={t.idRealEstateUseType}
                            >
                              {t.RealEstateUseType}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  </Grid>
                </Section>
                {/* Vermessung & Teilfläche */}
                <Section title='Vermessung & Teilfläche'>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <FormControl fullWidth size='small'>
                        <InputLabel>Fläche</InputLabel>
                        <Select
                          label='Fläche'
                          value={formValues.hasPartialArea ? 1 : 2}
                          onChange={({ target: { value } }) =>
                            setFormValues(x => ({
                              ...x,
                              hasPartialArea: Number(value) === 1,
                            }))
                          }
                        >
                          <MenuItem value={1}>Teilbereich</MenuItem>
                          <MenuItem value={2}>Ganzes Grundstück</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Collapse
                      in={formValues.hasPartialArea}
                      sx={{ width: '100%' }}
                    >
                      <Grid container spacing={2} sx={{ mt: 0.5, px: 2 }}>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size='small'>
                            <InputLabel>Vermessen</InputLabel>
                            <Select
                              label='Vermessen'
                              value={formValues.isMeasured ? 1 : 2}
                              onChange={({ target: { value } }) =>
                                setFormValues(x => ({
                                  ...x,
                                  isMeasured: Number(value) === 1,
                                }))
                              }
                            >
                              <MenuItem value={1}>Ja</MenuItem>
                              <MenuItem value={2}>Nein</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <Collapse in={formValues.isMeasured}>
                            <NumericFormat
                              value={formValues.Area ?? ''}
                              suffix=' m²'
                              thousandSeparator='.'
                              decimalSeparator=','
                              fullWidth
                              customInput={TextField}
                              size='small'
                              label='Fläche'
                              allowNegative={false}
                              onValueChange={values =>
                                setFormValues(x => ({
                                  ...x,
                                  Area: values.floatValue ?? null,
                                }))
                              }
                            />
                          </Collapse>
                          <Collapse in={!formValues.isMeasured}>
                            <FormControl fullWidth size='small'>
                              <InputLabel>
                                Auftraggeber/in Vermessung
                              </InputLabel>
                              <Select
                                label='Auftraggeber/in Vermessung'
                                value={
                                  purchaseContract?.OrderMeasurement_idPurchaseContractRelationType ??
                                  ''
                                }
                                onChange={({ target: { value } }) =>
                                  setPurchaseContract(x =>
                                    x
                                      ? {
                                          ...x,
                                          OrderMeasurement_idPurchaseContractRelationType:
                                            Number(value),
                                        }
                                      : x
                                  )
                                }
                              >
                                {(purchaseContractRelationTypes ?? []).map(
                                  p => (
                                    <MenuItem
                                      key={p.idPurchaseContractRelationType}
                                      value={p.idPurchaseContractRelationType}
                                    >
                                      {p.PurchaseContractRelationType}
                                    </MenuItem>
                                  )
                                )}
                              </Select>
                            </FormControl>
                          </Collapse>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size='small'>
                            <InputLabel>
                              Zahlungspflichtige/r Vermessung
                            </InputLabel>
                            <Select
                              label='Zahlungspflichtige/r Vermessung'
                              value={
                                purchaseContract?.PaymentMeasurement_idPurchaseContractRelationType ??
                                ''
                              }
                              onChange={({ target: { value } }) =>
                                setPurchaseContract(x =>
                                  x
                                    ? {
                                        ...x,
                                        PaymentMeasurement_idPurchaseContractRelationType:
                                          Number(value),
                                      }
                                    : x
                                )
                              }
                            >
                              {(purchaseContractRelationTypes ?? []).map(p => (
                                <MenuItem
                                  key={p.idPurchaseContractRelationType}
                                  value={p.idPurchaseContractRelationType}
                                >
                                  {p.PurchaseContractRelationType}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <Divider sx={{ my: 1 }}>Dienstbarkeiten</Divider>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                          <FormControl fullWidth size='small'>
                            <InputLabel>Dienstbarkeiten vorhanden?</InputLabel>
                            <Select
                              label='Dienstbarkeiten vorhanden?'
                              value={formValues.hasEasement ? 'true' : 'false'}
                              onChange={({ target: { value } }) =>
                                setFormValues(x => ({
                                  ...x,
                                  hasEasement: value === 'true',
                                }))
                              }
                            >
                              <MenuItem value='true'>Ja</MenuItem>
                              <MenuItem value='false'>Nein</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <Collapse in={formValues.hasEasement}>
                            <TextField
                              label='Beschreibung Dienstbarkeiten'
                              size='small'
                              fullWidth
                              multiline
                              rows={3}
                              value={formValues.EasementText ?? ''}
                              onChange={({ target }) =>
                                setFormValues(x => ({
                                  ...x,
                                  EasementText: target.value || null,
                                }))
                              }
                            />
                          </Collapse>
                        </Grid>
                      </Grid>
                    </Collapse>
                  </Grid>
                </Section>

                {/* Anlagen & Inventar */}
                <Section title='Anlagen & Inventar'>
                  <RealEstateInventoryOptions
                    inventoryOptionArray={purchaseContract.InventoryOptionArray}
                    setInventoryOptionArray={arr =>
                      setPurchaseContract(x =>
                        x ? { ...x, InventoryOptionArray: arr } : x
                      )
                    }
                    inventoryOptions={inventoryOptions ?? []}
                  />
                </Section>

                {/* Räumung */}
                <Section title='Räumung'>
                  <FormControl component='fieldset'>
                    <FormLabel>Räumung notwendig?</FormLabel>
                    <RadioGroup
                      row
                      value={purchaseContract.requiresClearance ? 'yes' : 'no'}
                      onChange={({ target }) =>
                        setPurchaseContract(x =>
                          x
                            ? {
                                ...x,
                                requiresClearance: target.value === 'yes',
                                clearanceDate:
                                  target.value === 'yes'
                                    ? x.clearanceDate
                                    : null,
                              }
                            : x
                        )
                      }
                    >
                      <FormControlLabel
                        value='yes'
                        control={<Radio />}
                        label='Ja'
                      />
                      <FormControlLabel
                        value='no'
                        control={<Radio />}
                        label='Nein'
                      />
                    </RadioGroup>
                  </FormControl>
                  <Collapse in={purchaseContract.requiresClearance}>
                    <Box sx={{ mt: 1, maxWidth: 300 }}>
                      <LocalizationProvider
                        dateAdapter={AdapterDayjs}
                        adapterLocale='de'
                      >
                        <DatePicker
                          label='Räumung bis (Datum)'
                          openTo='year'
                          views={['year', 'month', 'day']}
                          value={
                            purchaseContract.clearanceDate
                              ? dayjs(purchaseContract.clearanceDate)
                              : null
                          }
                          onChange={newValue =>
                            setPurchaseContract(x =>
                              x
                                ? {
                                    ...x,
                                    clearanceDate: newValue
                                      ? newValue.format('YYYY-MM-DD')
                                      : '',
                                  }
                                : x
                            )
                          }
                          format='DD.MM.YYYY'
                          slotProps={{
                            textField: { size: 'small', fullWidth: true },
                          }}
                        />
                      </LocalizationProvider>
                    </Box>
                  </Collapse>
                </Section>

                {/* Vermietung */}
                <Section title='Vermietung / Verpachtung'>
                  <FormControl component='fieldset'>
                    <FormLabel>Vermietet / Verpachtet?</FormLabel>
                    <RadioGroup
                      row
                      value={purchaseContract.isRented ? 'yes' : 'no'}
                      onChange={({ target }) =>
                        setPurchaseContract(x =>
                          x ? { ...x, isRented: target.value === 'yes' } : x
                        )
                      }
                    >
                      <FormControlLabel
                        value='yes'
                        control={<Radio />}
                        label='Ja'
                      />
                      <FormControlLabel
                        value='no'
                        control={<Radio />}
                        label='Nein'
                      />
                    </RadioGroup>
                  </FormControl>
                  <Collapse in={purchaseContract.isRented}>
                    <Box sx={{ mt: 2 }}>
                      {purchaseContract.TenantArray.length === 0 ? (
                        <Alert severity='info'>
                          Noch keine Mieter/Pächter erfasst.
                        </Alert>
                      ) : (
                        <Stack spacing={0.5}>
                          <Typography variant='subtitle2'>
                            Erfasste Mieter/Pächter:
                          </Typography>
                          {purchaseContract.TenantArray.map((p, idx) => (
                            <Typography
                              key={p.idLegalClient ?? idx}
                              variant='body2'
                              color='text.secondary'
                            >
                              •{' '}
                              {[p.FirstName, p.LastName]
                                .filter(Boolean)
                                .join(' ')}
                            </Typography>
                          ))}
                        </Stack>
                      )}
                    </Box>
                  </Collapse>
                </Section>
              </>
            )}
          </Stack>
        );

      case 'valuation':
        return (
          <Stack spacing={2}>
            <Alert severity='info' variant='outlined'>
              Bewertung: Der Bodenrichtwert lässt sich im Tab „Grundbuch" über
              BORIS ermitteln. Grundstückswert und weitere Bewertungsgrundlagen
              werden hier künftig erfasst.
            </Alert>
          </Stack>
        );

      case 'log': {
        const blattResolved = blattId ?? resolvedIdGrundbuchblatt;
        if (!blattResolved) {
          return (
            <Alert severity='info' variant='outlined'>
              Sobald ein Grundbuchblatt hinterlegt ist, werden hier alle
              Ereignisse (Vorgang-Verknüpfungen, Grundbuchabrufe,
              Eigentümer-Änderungen) protokolliert.
            </Alert>
          );
        }
        return (
          <>
            <ActivityLog
              entityType='Grundbuchblatt'
              entityId={blattResolved}
              areas={LAND_REGISTER_LOG_AREAS}
              rowsPerPage={15}
            />
          </>
        );
      }

      case 'object':
      default:
        return (
          <Stack spacing={0}>
            {/* Grundstücksart & Bebauung */}
            <Section
              title='Grundstücksart & Bebauung'
              defaultExpanded={formValues.PropertyTypeArray.length > 0}
            >
              <RealEstatePropertyTypes
                formValues={formValues}
                setFormValues={setFormValues}
                propertyTypes={propertyTypes ?? []}
              />
              {!isOnlyUndevelopedProperty && (
                <Box sx={{ mt: 4 }}>
                  <Typography variant='subtitle2' sx={{ mb: 1 }}>
                    Bebauung
                  </Typography>
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6}>
                      <Collapse in={!hasBuildPropertyType}>
                        <FormControl fullWidth size='small'>
                          <InputLabel>Bebaut</InputLabel>
                          <Select
                            label='Bebaut'
                            value={formValues.isBuild ? 1 : 2}
                            onChange={({ target: { value } }) =>
                              setFormValues(x => ({
                                ...x,
                                isBuild: Number(value) === 1,
                                BuildWith:
                                  Number(value) === 1 ? x.BuildWith : null,
                              }))
                            }
                          >
                            <MenuItem value={1}>Ja</MenuItem>
                            <MenuItem value={2}>Nein</MenuItem>
                          </Select>
                        </FormControl>
                      </Collapse>
                    </Grid>
                    <Grid item xs={12}>
                      <Collapse
                        in={!!formValues.isBuild || hasBuildPropertyType}
                      >
                        <TextField
                          label='Beschreibung der Bebauung'
                          size='small'
                          fullWidth
                          multiline
                          rows={3}
                          value={formValues.BuildWith ?? ''}
                          onChange={({ target }) =>
                            setFormValues(x => ({
                              ...x,
                              BuildWith: target.value || null,
                            }))
                          }
                        />
                      </Collapse>
                    </Grid>
                  </Grid>
                </Box>
              )}
            </Section>

            {/* Wohn- oder Teileigentum */}
            <Section title='Wohn- oder Teileigentum'>
              <FormControl component='fieldset'>
                <FormLabel>Liegt Wohn- oder Teileigentum vor?</FormLabel>
                <RadioGroup
                  row
                  value={formValues.hasApartment ? 'yes' : 'no'}
                  onChange={({ target }) =>
                    setFormValues(x => ({
                      ...x,
                      hasApartment: target.value === 'yes',
                    }))
                  }
                >
                  <FormControlLabel
                    value='yes'
                    control={<Radio />}
                    label='Ja'
                  />
                  <FormControlLabel
                    value='no'
                    control={<Radio />}
                    label='Nein'
                  />
                </RadioGroup>
              </FormControl>
            </Section>

            {/* Mängel & Besonderheiten */}
            <Section title='Mängel & Besonderheiten'>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label='Mängel / Einschränkungen der Nutzbarkeit'
                    size='small'
                    fullWidth
                    multiline
                    rows={3}
                    value={formValues.Defect ?? ''}
                    onChange={({ target }) =>
                      setFormValues(x => ({
                        ...x,
                        Defect: target.value || null,
                      }))
                    }
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label='Besonderheiten'
                    size='small'
                    fullWidth
                    multiline
                    rows={3}
                    value={formValues.Particularities ?? ''}
                    onChange={({ target }) =>
                      setFormValues(x => ({
                        ...x,
                        Particularities: target.value || null,
                      }))
                    }
                  />
                </Grid>
              </Grid>
            </Section>
          </Stack>
        );
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth='lg' fullWidth>
      <DialogTitle sx={{ pb: 1 }}>
        <Stack
          direction='row'
          justifyContent='space-between'
          alignItems='flex-start'
        >
          <Box>
            <Typography variant='h5'>
              {blattnummer
                ? `Grundbuchblatt ${blattnummer}`
                : addressTitle || `Grundstück #${formValues.idRealEstate}`}
            </Typography>
            {headChips.length > 0 && (
              <Stack
                direction='row'
                spacing={0.5}
                flexWrap='wrap'
                useFlexGap
                sx={{ mt: 0.5 }}
              >
                {headChips.map(c => (
                  <Chip key={c} size='small' variant='outlined' label={c} />
                ))}
              </Stack>
            )}
          </Box>
          <IconButton size='small' onClick={onClose}>
            <Close fontSize='small' />
          </IconButton>
        </Stack>
        {!hasBlatt && (
          <Alert severity='warning' icon={<Warning />} sx={{ mt: 1.5 }}>
            <AlertTitle>Keine Blattnummer hinterlegt</AlertTitle>
            Führen Sie im Bereich „Grundbuch" eine Eigentümerrecherche durch, um
            die Grundbuchinformationen zu ermitteln.
          </Alert>
        )}
      </DialogTitle>

      <DialogContent dividers sx={{ p: 0 }}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Typography variant='subtitle1' fontWeight={600} sx={{ mb: 1.5 }}>
            Grundbuchblatt
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              select
              label='Amtsgericht'
              size='small'
              value={blattCourt}
              onChange={e => {
                setBlattCourt(e.target.value ? Number(e.target.value) : '');
                setBlattAdminUnit('');
              }}
              sx={{ flex: 1 }}
            >
              <MenuItem value=''>—</MenuItem>
              {(courts ?? []).map(c => (
                <MenuItem key={c.idCourt} value={c.idCourt}>
                  {c.Court}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label='Gemarkung'
              size='small'
              value={blattAdminUnit}
              onChange={e => {
                const v = e.target.value ? Number(e.target.value) : '';
                setBlattAdminUnit(v);
                // Bidirektional: ist die Gemarkung gewählt, aber (noch) kein
                // Amtsgericht, das zugehörige Gericht (Rolle 3) automatisch setzen.
                if (v !== '' && !blattCourt) {
                  // Nur automatisch setzen, wenn die Gemarkung EINDEUTIG einem
                  // Gericht (Rolle 3) zugeordnet ist (#1173). Es gibt gleich-
                  // namige Gemarkungen (z. B. „Ahrensdorf" 4×) — teils ohne, teils
                  // mit mehreren Gericht-Zuordnungen. Bei 0 oder mehreren nicht
                  // willkürlich raten, sondern manuell wählen lassen.
                  const courtIds = Array.from(
                    new Set(
                      (courtAdministrativeUnitAssignments ?? [])
                        .filter(
                          a =>
                            a.idAdministrativeUnit === v && a.idCourtRole === 3
                        )
                        .map(a => a.idCourt)
                        .filter((id): id is number => id != null)
                    )
                  );
                  if (courtIds.length === 1) setBlattCourt(courtIds[0]);
                }
              }}
              sx={{ flex: 1 }}
            >
              <MenuItem value=''>—</MenuItem>
              {blattGemarkungOptions.map(u => (
                <MenuItem
                  key={u.idAdministrativeUnit}
                  value={u.idAdministrativeUnit}
                >
                  {u.AdministrativeUnit}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label='Blatt'
              size='small'
              value={blattnummer}
              onChange={e => setBlattnummer(e.target.value)}
              sx={{ flex: 1 }}
            />
          </Stack>
        </Box>

        <Grid container sx={{ minHeight: 480 }}>
          <Grid
            item
            xs={12}
            md={3}
            sx={{ borderRight: { md: 1 }, borderColor: { md: 'divider' } }}
          >
            <List dense sx={{ py: 1 }}>
              {MENU.map((m, i) => {
                // Vorerst deaktiviert (nicht klickbar)
                const disabled = (
                  ['owners', 'contract', 'valuation', 'object'] as string[]
                ).includes(m.key);
                return (
                  <ListItemButton
                    key={m.key}
                    selected={tab === i}
                    disabled={disabled}
                    onClick={() => setTab(i)}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>{m.icon}</ListItemIcon>
                    <ListItemText primary={m.label} />
                  </ListItemButton>
                );
              })}
            </List>
          </Grid>
          <Grid item xs={12} md={9} sx={{ p: 2 }}>
            {renderContent()}
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button variant='outlined' onClick={onClose} disabled={isSaving}>
          Abbruch
        </Button>
        <Button
          variant='contained'
          onClick={handleSave}
          disabled={!isAreaValid || isWarningPrice || isSaving}
        >
          Übernehmen
        </Button>
      </DialogActions>
    </Dialog>
  );
};
